<template>
  <div class="d-flex flex-wrap justify-between align-center" style="gap: 10px">
    <h1>Company Users</h1>

    <!-- Add Account Button (on the right side) -->
    <div
      class="d-flex justify-end"
      :class="$vuetify.display.mdAndDown ? 'flex-wrap' : 'ga-3'"
      style="gap: 0px; flex: 1"
    >
      <v-btn
        v-if="selectedRole === 'company'"
        data-testid="add-company-user-btn"
        @click="navigateToCreateUser"
        variant="text"
        size="small"
        prepend-icon="mdi-plus"
        class="text-grey-darken-2"
      >
        Add New Company User
      </v-btn>
    </div>
  </div>

  <p class="my-6 text-body-2 text-grey-darken-1">
    Manage company users. Use the actions menu to update roles, activate/deactivate, or delete.
  </p>

  <v-card>
    <v-toolbar color="primary" density="comfortable" flat>
      <!-- Filter Menu Button -->
      <v-menu offset-y v-model="filterMenuOpen" :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-btn
            data-testid="filter-menu-btn"
            v-bind="props"
            variant="text"
            class="text-white ml-2"
            prepend-icon="mdi-filter-variant"
          >
            Filter Roles
            <v-badge
              v-if="selectedRoles.length < 4"
              :content="selectedRoles.length"
              color="error"
              inline
              class="ml-2"
            />
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item>
            <v-checkbox
              data-testid="filter-user"
              v-model="tempSelectedRoles"
              value="user"
              density="compact"
              hide-details
              :label="`User (${roleCounts.user})`"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox
              data-testid="filter-admin"
              v-model="tempSelectedRoles"
              value="admin"
              density="compact"
              hide-details
              :label="`Admin (${roleCounts.admin})`"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox
              data-testid="filter-suadmin"
              v-model="tempSelectedRoles"
              value="suadmin"
              density="compact"
              hide-details
              :label="`Super Admin (${roleCounts.suadmin})`"
            />
          </v-list-item>
          <v-list-item>
            <v-checkbox
              data-testid="filter-owner"
              v-model="tempSelectedRoles"
              value="owner"
              density="compact"
              hide-details
              :label="`Owner (${roleCounts.owner})`"
            />
          </v-list-item>
          <v-divider class="my-2"></v-divider>
          <v-list-item>
            <div class="d-flex ga-2" style="width: 100%">
              <v-btn
                data-testid="filter-check-all-btn"
                size="small"
                variant="text"
                @click="checkAllFilters"
              >
                Check All
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                data-testid="filter-apply-btn"
                size="small"
                color="primary"
                variant="flat"
                @click="applyFilters"
              >
                Apply
              </v-btn>
            </div>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-spacer></v-spacer>
      <!-- Desktop search -->
      <v-text-field
        data-testid="user-search-input"
        v-if="!$vuetify.display.mdAndDown"
        ref="searchRef"
        v-model="searchQuery"
        placeholder="Search users…"
        clearable
        @click:clear="searchQuery = ''"
        prepend-inner-icon="mdi-magnify"
        hide-details
        density="compact"
        variant="solo"
        class="search-in-bar mr-2"
        style="max-width: 380px"
      ></v-text-field>
    </v-toolbar>
    <!-- Mobile search: full width on its own row -->
    <div class="px-2 pt-2 mb-2" v-if="$vuetify.display.mdAndDown">
      <v-text-field
        data-testid="user-search-input"
        ref="searchRef"
        v-model="searchQuery"
        placeholder="Search users…"
        clearable
        @click:clear="searchQuery = ''"
        prepend-inner-icon="mdi-magnify"
        hide-details
        density="comfortable"
        variant="solo"
        style="width: 100%; max-width: 100%"
      ></v-text-field>
    </div>
  </v-card>

  <!-- Users/Admins Table -->
  <v-data-table
    data-testid="company-users-table"
    :items-per-page="50"
    fixed-header
    style="max-width: 100%; width: 100%; overflow-x: auto"
    :headers="headers"
    :items="filteredDisplayedUsers"
    :loading="isLoading"
    class="elevation-1 sticky-header striped-rows even-cols"
    :style="{ '--even-col-count': headers.length }"
    item-value="username"
    loading-text="Loading users..."
  >
    <template #item.status="{ item }">
      <div class="d-flex align-center justify-center" style="width: 100%">
        <v-chip
          size="x-small"
          :color="item?.status === 'Verified' ? 'green' : 'red'"
          variant="tonal"
        >
          {{ item?.status }}
        </v-chip>
      </div>
    </template>

    <template #item.actions="{ item }">
      <div class="d-flex align-center">
        <!-- Add the 3-dot menu -->
        <v-menu offset-y>
          <template v-slot:activator="{ props }">
            <v-btn
              icon
              variant="text"
              size="x-small"
              class="subtle-action mr-2"
              color="grey-darken-1"
              v-bind="props"
              @click.stop
            >
              <v-icon size="20">mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>

          <v-list dense>
            <!-- Resend Verification Email (only for unverified users) -->
            <v-list-item
              v-if="item.status === 'Unverified'"
              data-testid="resend-verification-btn"
              @click.stop="resendVerification(item.username)"
              prepend-icon="mdi-email-sync"
            >
              Resend Verification Email
            </v-list-item>

            <v-list-item
              data-testid="update-role-btn"
              @click.stop="openRoleDialog(item)"
              prepend-icon="mdi-account-edit"
            >
              Update User Role
            </v-list-item>

            <!-- Toggle Active/Inactive -->
            <v-list-item
              data-testid="toggle-status-btn"
              @click.stop="promptToggle(item)"
              :prepend-icon="item.enabled ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off'"
            >
              {{ item.enabled ? 'Activated' : 'Deactivated' }}
            </v-list-item>

            <!-- Delete User -->
            <v-list-item
              data-testid="delete-user-btn"
              @click.stop="promptDelete(item.username)"
              prepend-icon="mdi-delete"
            >
              Delete User
            </v-list-item>

            <!-- Add any additional menu items here -->
          </v-list>
        </v-menu>
      </div>
    </template>
  </v-data-table>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="deleteDialog" max-width="400px" data-testid="delete-dialog">
    <v-card>
      <v-card-title style="font-weight: 600" class="headline">Confirm Delete</v-card-title>
      <v-card-text>
        Are you sure you want to permanently delete user
        <strong>{{ userToDelete }}</strong>
        ?
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn data-testid="delete-dialog-cancel" text @click="deleteDialog = false">Cancel</v-btn>
        <v-btn
          data-testid="delete-dialog-confirm"
          color="red"
          text
          @click="selectedRole == 'clients' ? confirmDeleteClients() : confirmDelete()"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="toggleDialog" max-width="400px" data-testid="toggle-dialog">
    <v-card>
      <v-card-title class="headline">
        {{ toggleTarget.enabled ? 'Deactivate' : 'Activate' }} "{{ toggleTarget.username }}"?
      </v-card-title>
      <v-card-text>
        Are you sure you want to
        <strong>{{ toggleTarget.enabled ? 'deactivate' : 'activate' }}</strong>
        this account?
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn data-testid="toggle-dialog-cancel" text @click="toggleDialog = false">Cancel</v-btn>
        <v-btn
          data-testid="toggle-dialog-confirm"
          color="primary"
          text
          @click="selectedRole == 'clients' ? confirmToggleClient() : confirmToggle()"
        >
          {{ toggleTarget.enabled ? 'Deactivate' : 'Activate' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="roleDialog" max-width="400px" data-testid="role-dialog">
    <v-card>
      <v-card-title class="headline">Update Role</v-card-title>
      <v-card-text>
        <div>
          Change role for
          <strong>{{ userToUpdateRole?.username }}</strong>
          , Current role is
          <strong>{{ userToUpdateRole?.customRole }}</strong>
          &nbsp;
          <br />
          &nbsp;
          <v-select
            data-testid="role-select"
            v-model="selectedNewRole"
            :items="availableGroupOptions"
            item-title="title"
            item-value="value"
            label="Select new role"
            required
          ></v-select>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn data-testid="role-dialog-cancel" text @click="roleDialog = false">Cancel</v-btn>
        <v-btn data-testid="role-dialog-confirm" color="primary" text @click="confirmUpdateRole">
          Update
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-snackbar v-model="snackbar" color="info" :timeout="3000">
    {{ message }}
    <template v-slot:action="{ attrs }">
      <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Close</v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import router from '@/router'
import { useRoute } from 'vue-router'
import {
  fetchAllUsers,
  fetchAllClients,
  deleteUser,
  deleteUserClients,
  toggleUserStatusSetActiveNow,
  toggleUserStatusSetDeActiveNow,
  toggleUserStatusSetActiveNowClient,
  toggleUserStatusSetDeActiveNowClient,
  updateRole,
  VerifyAdmin,
  resendVerificationEmailCompany,
} from '@/api/accountsAdmin'
import { useAccountStore } from '@/stores/accountStore'
import ProgressLoader from './ProgressLoader.vue'

const isLoading = ref(true)
const accountStore = useAccountStore()
const allUsers = ref([])
const allCLients = ref([])
const selectedRole = ref('company')
import { useUserStore } from '@/stores/userStore'
const userStore = useUserStore()
const deleteDialog = ref(false)
const dialogConfirm = ref(false)
const userToDelete = ref(null)

const toggleDialog = ref(false)
const toggleTarget = ref({ username: '', enabled: false })

import Cookies from 'universal-cookie'
const cookies = new Cookies()

const admin = cookies.get('admin')
const myRole = userStore?.role
const message = ref('')
const snackbar = ref(false)

// Search
const searchQuery = ref('')
const searchRef = ref()
const tab = ref('all')
// Role checkbox state (default all selected)
const selectedRoles = ref(['user', 'admin', 'suadmin', 'owner'])
const tempSelectedRoles = ref(['user', 'admin', 'suadmin', 'owner'])
const filterMenuOpen = ref(false)

const roleDialog = ref(false)
const userToUpdateRole = ref(null)
const selectedNewRole = ref('')

function openRoleDialog(user) {
  userToUpdateRole.value = user
  selectedNewRole.value = user?.customRoleKey || '' // use raw key as value
  roleDialog.value = true
}
async function confirmUpdateRole() {
  try {
    if (!selectedNewRole.value || !userToUpdateRole.value?.username) return

    const username = userToUpdateRole.value.username
    const newRole = selectedNewRole.value
    
    await updateRole(username, newRole, false)
    
    // Optimistically update the UI without full reload
    const friendlyRoleName = toFriendlyRoleName(newRole)
    
    if (selectedRole.value === 'clients') {
      const clientIndex = allCLients.value.findIndex(u => u.username === username)
      if (clientIndex !== -1) {
        allCLients.value[clientIndex].customRoleKey = newRole
        allCLients.value[clientIndex].customRole = friendlyRoleName
        // Update cache
        accountStore.setClientUsersCache(allCLients.value)
      }
    } else {
      const userIndex = allUsers.value.findIndex(u => u.username === username)
      if (userIndex !== -1) {
        allUsers.value[userIndex].customRoleKey = newRole
        allUsers.value[userIndex].customRole = friendlyRoleName
        // Update cache
        accountStore.setCompanyUsersCache(allUsers.value)
      }
    }
    
    message.value = '✅ Role updated successfully.'
    snackbar.value = true
  } catch (e) {
    console.error('Failed to update role:', e)
    message.value = '❌ Failed to update role.'
    snackbar.value = true
  } finally {
    roleDialog.value = false
  }
}

const allGroups = ['company_user', 'company_admin', 'company_suadmin', 'company_owner']

const availableGroups = computed(() => {
  switch (userStore?.role) {
    case 'company_owner':
      return allGroups
    case 'company_suadmin':
      // suadmin sees user, admin, suadmin
      return allGroups.filter((g) => g !== 'company_owner')
    case 'company_admin':
      // admin sees user + admin
      return allGroups.filter((g) => ['company_admin', 'company_user'].includes(g))
    default:
      // a normal user only sees themselves
      return ['company_user']
  }
})

// Convert role keys (e.g., "company_suadmin") to friendly display names (e.g., "Super Admin")
function toFriendlyRoleName(roleKey) {
  if (!roleKey) return ''
  const key = String(roleKey).replace(/^(company|client)_/, '')
  const map = {
    owner: 'Owner',
    suadmin: 'Super Admin',
    admin: 'Admin',
    manager: 'Manager',
    user: 'User',
  }
  if (map[key]) return map[key]
  // Fallback: prettify any other keys
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

// Items for the role picker with friendly titles but raw values
const availableGroupOptions = computed(() =>
  availableGroups.value.map((v) => ({ title: toFriendlyRoleName(v), value: v }))
)

const hierarchy = ['company_user', 'company_admin', 'company_suadmin', 'company_owner']
const myLevel = hierarchy.indexOf(myRole)
function formatDate(raw) {
  // First normalize your two formats into a real Date()
  let dt
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    dt = new Date(raw)
  } else {
    // handle "YYYY-MM-DD hh:mm AM/PM"
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (m) {
      let [, y, M, d, h, mm, ampm] = m
      h = parseInt(h, 10)
      if (ampm.toUpperCase() === 'PM' && h !== 12) h += 12
      if (ampm.toUpperCase() === 'AM' && h === 12) h = 0
      dt = new Date(parseInt(y, 10), parseInt(M, 10) - 1, parseInt(d, 10), h, parseInt(mm, 10))
    } else {
      // fallback
      dt = new Date(raw)
    }
  }

  if (isNaN(dt)) return 'Invalid date'
  // now format however you like—here’s US‐style with full date & time:
  return dt.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

// Counts based on *displayed* list (company vs clients), not filtered by search
const totalUsers = computed(() => displayedUsers.value.length)

const roleCounts = computed(() => {
  const counts = { owner: 0, suadmin: 0, admin: 0, user: 0 }
  for (const u of displayedUsers.value) {
    const key = (u.customRoleKey || '').replace(/^company_/, '') // owner|suadmin|admin|user
    if (counts[key] != null) counts[key]++
  }
  return counts
})

const loadUsers = async () => {
  isLoading.value = true
  try {
    // Use cache if present
    if (accountStore.companyUsersCache && accountStore.companyUsersCache.length) {
      allUsers.value = accountStore.companyUsersCache.slice()
      return
    }
    const data = await fetchAllUsers()
    allUsers.value = data
      .filter((u) => {
        const roleAttr = u.Attributes.find((a) => a.Name === 'custom:role')?.Value
        const lvl = roleAttr ? hierarchy.indexOf(roleAttr) : 0
        return lvl <= myLevel
      })
      .map((user) => {
        const roleRaw =
          user?.Attributes.find((a) => a.Name === 'custom:role')?.Value || 'company_user'

        return {
          username: user.Username,
          email: user.Attributes.find((attr) => attr.Name === 'email')?.Value || 'N/A',
          userCreateDate: formatDate(user.UserCreateDate),
          userLastModifiedDate: formatDate(user.UserLastModifiedDate),
          enabled: user?.Enabled,
          status: user?.UserStatus === 'CONFIRMED' ? 'Verified' : 'Unverified',
          customRoleKey: roleRaw, // e.g. "company_admin"
          customRole: toFriendlyRoleName(roleRaw), // e.g. "Admin"
        }
      })
    accountStore.setCompanyUsersCache(allUsers.value)
  } catch (error) {
    console.error('Failed to load users:', error)
  } finally {
    isLoading.value = false
  }
}
const formatDatee = (isoString) => {
  return new Date(isoString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const loadAllClients = async () => {
  isLoading.value = true
  try {
    if (accountStore.companyClientsCache && accountStore.companyClientsCache.length) {
      allCLients.value = accountStore.companyClientsCache.slice()
      return
    }
    const data = await fetchAllClients()
    allCLients.value = data.map((admin) => {
      const roleRaw =
        admin?.Attributes.find((a) => a.Name === 'custom:role')?.Value || 'company_user'

      return {
        username: admin.Username,
        email: admin.Attributes.find((attr) => attr.Name === 'email')?.Value || 'N/A',
        userCreateDate: formatDate(admin.UserCreateDate),
        userLastModifiedDate: formatDate(admin.UserLastModifiedDate),
        enabled: admin?.Enabled,
        status: admin?.UserStatus === 'CONFIRMED' ? 'Verified' : 'Unverified',
        customRoleKey: roleRaw,
        customRole: toFriendlyRoleName(roleRaw),
      }
    })
    accountStore.setCompanyClientsCache(allCLients.value)
  } catch (error) {
    console.error('Failed to load admins:', error)
  } finally {
    isLoading.value = false
  }
}

// Computed property to switch between users and admins
const displayedUsers = computed(() =>
  selectedRole.value === 'company' ? allUsers.value : allCLients.value
)

const filteredDisplayedUsers = computed(() => {
  const q = String(searchQuery.value ?? '')
    .trim()
    .toLowerCase()
  const roles = new Set((selectedRoles.value || []).map((r) => String(r).toLowerCase()))
  // Start from base list (company vs clients)
  let list = displayedUsers.value
  // Filter by selected roles (checkboxes)
  list = list.filter((u) =>
    roles.has(String((u.customRoleKey || '').replace(/^company_/, '')).toLowerCase())
  )
  // Then apply search filter
  if (!q) return list
  return list.filter(
    (u) =>
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.customRole || '').toLowerCase().includes(q)
  )
})

const route = useRoute()

onMounted(() => {
  VerifyAdmin()

  // Check if we're coming from user creation (via referrer or query param)
  const fromCreate =
    document.referrer.includes('/users/create-company-user') ||
    document.referrer.includes('/users/create-client-user-simple') ||
    route.query.refresh

  if (fromCreate) {
    console.log('Mounted after user creation, clearing cache and forcing refresh...')
    // Clear cache to force fresh data
    accountStore.setCompanyUsersCache([])
    accountStore.setCompanyClientsCache([])

    // Clear the query param from URL without triggering navigation
    if (route.query.refresh) {
      router.replace({ path: route.path, query: {} })
    }
  }

  loadUsers()
  loadAllClients()
})

// Watch for route changes to refresh data when returning from user creation
watch(
  () => route.path,
  async (newPath, oldPath) => {
    // Reload data when navigating to this page from user creation
    if (newPath === '/company-accounts' && oldPath && oldPath.includes('create')) {
      console.log('Returning from user creation, clearing cache and refreshing data...')
      // Clear cache and force reload
      try {
        accountStore.setCompanyUsersCache([])
        accountStore.setCompanyClientsCache([])
        console.log('Cache cleared, fetching fresh data...')

        // Set loading state
        isLoading.value = true

        // Force fresh fetch by clearing local data first
        allUsers.value = []
        allCLients.value = []

        // Fetch fresh data
        await loadUsers()
        await loadAllClients()

        console.log('Fresh data loaded, users count:', allUsers.value.length)
      } catch (error) {
        console.error('Error refreshing data:', error)
      } finally {
        isLoading.value = false
      }
    }
  }
)

// Focus search after table loads
watch(isLoading, (val, oldVal) => {
  if (oldVal && !val) {
    setTimeout(() => {
      try {
        searchRef.value?.focus?.()
      } catch {}
    }, 0)
  }
})

const headers = [
  { title: 'Username', align: 'start', key: 'username' },
  { title: 'Email', key: 'email' },
  { title: 'Role', key: 'customRole', align: 'center' },
  { title: 'Status', key: 'status', align: 'center' },
  { title: 'Actions', key: 'actions', align: 'start' },
]

const navigateToCreateUser = () => {
  console.log('Navigating to create company user...', router)
  router.push({ name: 'CreateCompanyUser' })
}

function promptDelete(item) {
  console.log(item)
  userToDelete.value = item
  deleteDialog.value = true
}
async function confirmDelete() {
  console.log(userToDelete.value)
  if (!userToDelete.value) return
  try {
    await deleteUser(userToDelete.value)
    // refresh the list
    if (selectedRole.value === 'company') await loadUsers()
    else await loadAdmins()
    // Invalidate caches
    accountStore.setCompanyUsersCache([])
    accountStore.setClientUsersCache([])
    // Optimistically remove from current table without full reload
    if (selectedRole.value === 'company') {
      allUsers.value = allUsers.value.filter(
        (u) => String(u.username) !== String(userToDelete.value)
      )
      accountStore.setCompanyUsersCache(allUsers.value)
    } else {
      allCLients.value = allCLients.value.filter(
        (u) => String(u.username) !== String(userToDelete.value)
      )
      accountStore.setCompanyClientsCache(allCLients.value)
    }
    message.value = 'User deleted successfully.'
    snackbar.value = true
  } catch (err) {
    console.error('Failed to delete', err)
  } finally {
    deleteDialog.value = false
    userToDelete.value = null
  }
}

function promptToggle(item) {
  toggleTarget.value = { username: item.username, enabled: item.enabled }
  console.log(toggleTarget.value)
  toggleDialog.value = true
}
async function confirmToggle() {
  try {
    const username = toggleTarget.value.username
    const currentEnabled = toggleTarget.value.enabled
    
    if (currentEnabled) {
      // currently enabled → deactivate
      await toggleUserStatusSetDeActiveNow(username)
      message.value = 'User Deactivated Successfully'
    } else {
      // currently disabled → activate
      await toggleUserStatusSetActiveNow(toggleTarget.value.username)
      message.value = 'User Activate Successfully'
      snackbar.value = true
    }
    // Clear cache and refresh list
    accountStore.setCompanyUsersCache([])
    await loadUsers()
  } catch (e) {
    console.error(e)
    message.value = 'Unable to Toggle User'
    snackbar.value = true
  } finally {
    toggleDialog.value = false
  }
}

async function confirmToggleClient() {
  try {
    if (toggleTarget.value.enabled) {
      // currently enabled → deactivate
      await toggleUserStatusSetDeActiveNowClient(toggleTarget.value.username)
      message.value = 'User Deactivate Successfully'
      snackbar.value = true
    } else {
      // currently disabled → activate
      await toggleUserStatusSetActiveNowClient(toggleTarget.value.username)
      message.value = 'User Activate Successfully'
      snackbar.value = true
    }
    // Clear cache and refresh list
    accountStore.setClientUsersCache([])
    await loadAllClients()
  } catch (e) {
    console.error(e)
    message.value = 'Unable to Toggle User'
    snackbar.value = true
  } finally {
    toggleDialog.value = false
  }
}

async function confirmDeleteClients() {
  console.log(userToDelete.value)
  // if (!userToDelete.value) return
  try {
    await deleteUserClients(userToDelete.value)
    // refresh the list
    if (selectedRole.value === 'clients') await loadAllClients()
    // Optimistically remove from current table without full reload
    allCLients.value = allCLients.value.filter(
      (u) => String(u.username) !== String(userToDelete.value)
    )
    accountStore.setCompanyClientsCache(allCLients.value)
    message.value = 'User deleted successfully.'
    snackbar.value = true
  } catch (err) {
    console.error('Failed to delete', err)
  } finally {
    deleteDialog.value = false
    userToDelete.value = null
  }
}

// Filter functions
function applyFilters() {
  selectedRoles.value = [...tempSelectedRoles.value]
  filterMenuOpen.value = false
}

function checkAllFilters() {
  tempSelectedRoles.value = ['user', 'admin', 'suadmin', 'owner']
}

// Resend verification email
const resendingVerification = ref(false)

async function resendVerification(username) {
  if (resendingVerification.value) return

  try {
    resendingVerification.value = true
    await resendVerificationEmailCompany(username)
    message.value = 'Verification email sent successfully!'
    snackbar.value = true
  } catch (error) {
    console.error('Failed to resend verification email:', error)
    message.value =
      error?.response?.data?.message || 'Failed to send verification email. Please try again.'
    snackbar.value = true
  } finally {
    resendingVerification.value = false
  }
}
</script>

<style scoped>
/* Add any custom styling here */
:deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}

.v-data-table {
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
  /* min-width: 700px; */
}

.status-text {
  font-weight: 600;
}
.status-active {
  color: #2e7d32;
}
.status-inactive {
  color: #c62828;
}

.custom-button {
  width: 100%;

  height: 57px;
}

@media (max-width: 600px) {
  .custom-button {
    margin-top: 15px;
    width: 100%;
  }
}

/* Add this to your style section */
:deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}

:deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}

:deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}

/* Optional: Add "Inactive" indicator for mobile view */
:deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

:deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
  /* Very light gray - adjust to match your theme */
}

/* Hover effect */
:deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
  /* Slightly darker on hover */
}

/* Ensure text remains readable on striped rows */
:deep(.striped-rows .v-data-table__tr) {
  color: inherit;
  /* Inherits from parent */
}

:deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  /* Match your theme */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.even-cols :deep(.v-data-table__wrapper table) {
  table-layout: fixed;
}

.even-cols :deep(th),
.even-cols :deep(td) {
  width: calc(100% / var(--even-col-count, 5));
}
</style>
